/**
 * AI Question Map。想定質問のひな形と、ひな形ごとのカバレッジ。
 *
 * この面が守っていること:
 * - **数えていないものを 0 と描かない。** `answerCount` は API が必ず null で
 *   返すので「数えていません」と理由を出す。`coverage` が null の行も同じ
 * - **ひな形とルールを文字列で結ばない。** 担当ルールはピッカーから選ぶ
 * - **共通ひな形をでっち上げない。** 正本が無いので、並ぶのはこの会社で
 *   足した問いだけであることを書く
 * - 数え直しの導線は 1 つだけにする。この画面に「数え直す」ボタンを置くと、
 *   ペルソナ別の充足と 2 か所になる（数えているのは同じ実行）
 */

import { useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery } from "urql";
import {
  AddQuestionTemplateDocument,
  Button,
  Card,
  ErrorState,
  LoadingState,
  Meter,
  PageHeader,
  QuestionMapDocument,
  SetQuestionTemplateEnabledDocument,
  Tag,
  type PopulationId,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/HelpIcon";

import {
  MAX_QUESTION_SCOPE_LENGTH,
  MAX_QUESTION_TEXT_LENGTH,
  NOT_MEASURED,
  QUESTION_COVERAGE_SHARES_PERSONA_JOB_NOTE,
  QUESTION_EVIDENCE_BREAKDOWN_NOTE,
  SHARED_QUESTION_TEMPLATES_NOTE,
} from "../api-capabilities";
import { PopulationSelect } from "./PopulationSelect";

interface PageUserError {
  readonly code: string;
  readonly message: string;
}

const ORIGIN_LABEL: Record<string, string> = {
  SHARED_TEMPLATE: "visumo の共通ひな形",
  TENANT_ADDED: "この会社で足した",
};

const RULE_STATE_LABEL: Record<string, string> = {
  RUN: "配信している",
  STOP: "止めている",
};

function percent(rate: number | null | undefined): string {
  return rate == null ? NOT_MEASURED : `${Math.round(rate * 100)} %`;
}

export function QuestionMapPage() {
  // 1 画面 1 母数。切り替えると下のカバレッジはすべてその母数のものになる
  const [population, setPopulation] = useState<PopulationId>("DELIVERED");
  const [result, refetch] = useQuery({
    query: QuestionMapDocument,
    variables: { population },
    // ひな形はほかの端末からも増える。開き直したら読み直す
    requestPolicy: "cache-and-network",
  });
  const [addState, addTemplate] = useMutation(AddQuestionTemplateDocument);
  const [toggleState, setEnabled] = useMutation(
    SetQuestionTemplateEnabledDocument,
  );
  const [userErrors, setUserErrors] = useState<PageUserError[]>([]);
  const [question, setQuestion] = useState("");
  const [scope, setScope] = useState("");
  const [ruleId, setRuleId] = useState("");

  if (result.fetching && result.data === undefined) {
    return <LoadingState />;
  }
  if (result.error !== undefined || result.data === undefined) {
    return (
      <ErrorState
        description={result.error?.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  }

  const templates = result.data.questionTemplates;
  const coverage = result.data.questionCoverage;
  const rules = result.data.rules;
  const busy = addState.fetching || toggleState.fetching;
  const enabledCount = templates.filter((template) => template.enabled).length;
  // まだ数えていない行。0 % と区別して数える
  const notCounted = coverage.filter((row) => row.coverage == null).length;

  const failWith = (message: string): void => {
    setUserErrors([{ code: "INTERNAL_ERROR", message }]);
  };

  const submitNewQuestion = (): void => {
    const trimmed = question.trim();
    if (trimmed.length === 0) return;
    void addTemplate({
      input: {
        question: trimmed,
        ...(scope.trim().length === 0 ? {} : { scope: scope.trim() }),
        ...(ruleId === "" ? {} : { ruleId }),
      },
    }).then((response) => {
      if (response.error !== undefined) {
        failWith(`問いを足せませんでした: ${String(response.error)}`);
        return;
      }
      const payload = response.data?.addQuestionTemplate;
      if (payload === undefined) return;
      if (payload.userErrors.length > 0) {
        setUserErrors(
          payload.userErrors.map(({ code, message }) => ({ code, message })),
        );
        return;
      }
      setUserErrors([]);
      setQuestion("");
      setScope("");
      setRuleId("");
      refetch({ requestPolicy: "network-only" });
    });
  };

  const toggle = (templateId: string, next: boolean): void => {
    void setEnabled({ input: { templateId, enabled: next } }).then(
      (response) => {
        if (response.error !== undefined) {
          failWith(`切り替えられませんでした: ${String(response.error)}`);
          return;
        }
        const payload = response.data?.setQuestionTemplateEnabled;
        if (payload === undefined) return;
        if (payload.userErrors.length > 0) {
          setUserErrors(
            payload.userErrors.map(({ code, message }) => ({ code, message })),
          );
          return;
        }
        setUserErrors([]);
        refetch({ requestPolicy: "network-only" });
      },
    );
  };

  return (
    <div className="vd-page">
      <PageHeader
        title="AI Question Map"
        description="お客さまが聞きそうなことを並べ、それぞれに答えられる材料が揃っているかを見ます。"
        titleExtra={<HelpIcon slug="aio/question-map" />}
      />

      <div className="vd-stack">
        {userErrors.length === 0 ? null : (
          <div className="vd-notice vd-notice--error" role="alert">
            {userErrors.map((error) => (
              <p key={`${error.code}-${error.message}`} style={{ margin: 0 }}>
                {error.message}
              </p>
            ))}
          </div>
        )}

        <Card
          title="どの商品を母数にするか"
          note="1 つの画面では 1 つの母数だけを使います。選べるのは、いま API が数えられる母集団だけです。"
        >
          <PopulationSelect value={population} onChange={setPopulation} />
        </Card>

        <Card
          title="問いのひな形"
          note={SHARED_QUESTION_TEMPLATES_NOTE}
          extra={<HelpIcon slug="aio/question-map" anchor="問いのひな形" />}
        >
          {templates.length === 0 ? (
            <p className="vd-card-note" style={{ marginTop: 0 }}>
              まだ問いを 1 つも登録していません。下の欄から足してください。
            </p>
          ) : (
            <div className="vd-table-scroll">
              <table className="vd-table">
                <thead>
                  <tr>
                    <th>問い</th>
                    <th>出どころ</th>
                    <th>対象カテゴリ</th>
                    <th>担当するルール</th>
                    <th>何件の答えになったか</th>
                    <th>この会社で</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((template) => {
                    const rule =
                      template.ruleId === null
                        ? null
                        : (rules.find((item) => item.id === template.ruleId) ??
                          null);
                    return (
                      <tr key={template.id}>
                        <td>{template.question}</td>
                        <td>
                          {ORIGIN_LABEL[template.origin] ?? template.origin}
                        </td>
                        <td>{template.scope ?? "すべて"}</td>
                        <td>
                          {/* 内部の識別子を出さない。ルール名と状態で書く */}
                          {rule === null ? (
                            <span className="vd-stat-note">結んでいません</span>
                          ) : (
                            <>
                              <Link to={`/rules/${rule.id}`}>{rule.name}</Link>{" "}
                              <Tag
                                tone={
                                  template.ruleState === "RUN"
                                    ? "ok"
                                    : "neutral"
                                }
                              >
                                {template.ruleState === null
                                  ? "状態が分かりません"
                                  : (RULE_STATE_LABEL[template.ruleState] ??
                                    template.ruleState)}
                              </Tag>
                            </>
                          )}
                        </td>
                        <td>
                          {/* API は必ず null を返す。0 と描かない */}
                          <span className="vd-stat-note">
                            {template.answerCount ?? NOT_MEASURED}
                          </span>
                        </td>
                        <td>
                          <Button
                            disabled={busy}
                            aria-label={`「${template.question}」を${template.enabled ? "止める" : "使う"}`}
                            onClick={() =>
                              toggle(template.id, !template.enabled)
                            }
                          >
                            {template.enabled ? "止める" : "使う"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="vd-stat-note" style={{ marginBottom: 0 }}>
            {templates.length} 件のうち {enabledCount} 件を使っています。
            {templates[0]?.note ?? ""}
          </p>
        </Card>

        <Card
          title="この会社で問いを足す"
          extra={
            <HelpIcon slug="aio/question-map" anchor="この会社で問いを足す" />
          }
          note="担当させるルールは一覧から選びます。問いの文言とルール名が似ているだけでは結びません。"
        >
          <div className="vd-stack">
            <label className="vd-field">
              <span className="vd-field-hint">問い</span>
              <input
                className="vd-input"
                value={question}
                maxLength={MAX_QUESTION_TEXT_LENGTH}
                placeholder="例: 洗濯機で洗えますか"
                onChange={(event) => setQuestion(event.target.value)}
              />
            </label>
            <label className="vd-field">
              <span className="vd-field-hint">対象カテゴリ（任意）</span>
              <input
                className="vd-input"
                value={scope}
                maxLength={MAX_QUESTION_SCOPE_LENGTH}
                placeholder="空のままなら、すべての商品が対象です"
                onChange={(event) => setScope(event.target.value)}
              />
            </label>
            <label className="vd-field">
              <span className="vd-field-hint">担当するルール（任意）</span>
              <select
                className="vd-select"
                value={ruleId}
                onChange={(event) => setRuleId(event.target.value)}
              >
                <option value="">結ばない</option>
                {rules.map((rule) => (
                  <option key={rule.id} value={rule.id}>
                    {rule.name}（{RULE_STATE_LABEL[rule.state] ?? rule.state}）
                  </option>
                ))}
              </select>
            </label>
            <div>
              <Button
                variant="primary"
                disabled={busy || question.trim().length === 0}
                onClick={submitNewQuestion}
              >
                問いを足す
              </Button>
            </div>
          </div>
        </Card>

        <Card
          title="問いごとに、答えられる材料が揃っているか"
          extra={
            <HelpIcon
              slug="aio/question-map"
              anchor="問いごとに答えられる材料が揃っているか"
            />
          }
          note={QUESTION_COVERAGE_SHARES_PERSONA_JOB_NOTE}
        >
          {coverage.length === 0 ? (
            <p className="vd-card-note" style={{ marginTop: 0 }}>
              使っている問いがありません。上で問いを足すか、止めた問いを「使う」に戻してください。
            </p>
          ) : (
            <div className="vd-stack">
              {coverage.map((row) => (
                <div
                  key={row.question}
                  // 通しが 1 行だけを指せるようにする。行の入れ子は
                  // 文言で絞ると外側の区画にも当たる
                  data-testid="question-coverage-row"
                  style={{
                    borderTop: "1px solid var(--vd-border)",
                    paddingTop: 12,
                  }}
                >
                  <div
                    className="vd-row"
                    style={{ justifyContent: "space-between" }}
                  >
                    <div className="vd-card-title">{row.question}</div>
                    <div style={{ textAlign: "right" }}>
                      <div className="vd-stat-value">
                        {percent(row.coverage)}
                      </div>
                      <div className="vd-stat-note">
                        {row.basis.denominator === null
                          ? row.basis.label
                          : `${row.basis.label} ${row.basis.denominator.toLocaleString("ja-JP")} 件`}
                      </div>
                    </div>
                  </div>
                  {row.coverage == null ? null : (
                    <div style={{ maxWidth: 320 }}>
                      <Meter ratio={row.coverage} />
                    </div>
                  )}
                  <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
                    {row.basis.note}
                  </p>
                  {row.available.length === 0 ? null : (
                    <p className="vd-stat-note" style={{ margin: "2px 0 0" }}>
                      答えられている材料: {row.available.join(" ・ ")}
                    </p>
                  )}
                  {row.missing.length === 0 ? null : (
                    <p className="vd-stat-note" style={{ margin: "2px 0 0" }}>
                      足りない材料: {row.missing.join(" ・ ")}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="vd-stat-note" style={{ marginBottom: 0 }}>
            {notCounted === 0
              ? null
              : `${notCounted} 件はまだ数えていません（0 % ではありません）。`}
            数え直しは
            <Link to="/personas/coverage">ペルソナ別の充足</Link>
            から実行します。
          </p>
        </Card>

        <Card
          title="答えの根拠は何から来ているか"
          extra={
            <HelpIcon
              slug="aio/question-map"
              anchor="答えの根拠は何から来ているか"
            />
          }
          note={QUESTION_EVIDENCE_BREAKDOWN_NOTE}
        >
          <p style={{ margin: 0 }}>{NOT_MEASURED}</p>
        </Card>
      </div>
    </div>
  );
}
